"use client";

import { FlexColumn } from "@/components/Flex";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { useMedia } from "@knowt/syncing/hooks/media/useMedia";
import { useNote } from "@knowt/syncing/hooks/notes/useNotes";
import { Ban, CircleAlertIcon, PlusIcon, ReplaceIcon, RotateCw, SparkleIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import { FlashcardChatOptionLabels, FlashcardChatOptionPrompts } from "@knowt/syncing/hooks/ai/constants";
import toast from "react-hot-toast";
import { useSetEditingContextSelector } from "../hooks/useSetEditingContext";
import { useRerenderFlashcardList } from "./SetEditingFlashcardList";
import { useChatEditorCommunicator } from "@/features/AI/KaiChat/hooks/useChatEditorCommunicator";
import KaiChatInterface from "@/features/AI/KaiChat/KaiChatInterface";
import { ChatFlowState, ChatSuggestionOpt } from "@/features/AI/KaiChat/types";
import useConfirmDialog from "@/hooks/useConfirmDialog";
import { htmlToMd } from "@knowt/editor/md/parsing/parse";

const SetEditingChatInterface = () => {
    const flashcardSet = useSetEditingContextSelector(s => s.flashcardSet);
    const readOnly = useSetEditingContextSelector(s => s.readOnly);

    const { stringifiedTranscript } = useMedia({
        mediaId: flashcardSet?.mediaId,
        isEnabled: !!flashcardSet?.mediaId,
    });

    const { note } = useNote({ noteId: flashcardSet?.noteId, isEnabled: !!flashcardSet?.noteId });

    const { rerenderFlashcardList } = useRerenderFlashcardList();

    const chatLog = useChatEditorCommunicator(s => s.chatLog);
    const handleUnselect = useChatEditorCommunicator(s => s.handleUnselect);
    const actionToFlashcard = useChatEditorCommunicator(s => s.actionToFlashcard);
    const setChatFlowState = useChatEditorCommunicator(s => s.setChatFlowState);
    const setFullContent = useChatEditorCommunicator(s => s.setFullContent);
    const sourceContent = useChatEditorCommunicator(s => s.sourceContent);
    const setSourceContent = useChatEditorCommunicator(s => s.setSourceContent);
    const selectedContent = useChatEditorCommunicator(s => s.selectedContent);
    const stagePrompt = useChatEditorCommunicator(s => s.stagePrompt);
    const processChat = useChatEditorCommunicator(s => s.processChat);
    const isEmptyFlashcardSelected = useChatEditorCommunicator(s => s.isEmptyFlashcardSelected);

    const { openConfirmDialog } = useConfirmDialog();

    const addFlashcards = useSetEditingContextSelector(s => s.addFlashcards);
    const updateFlashcards = useSetEditingContextSelector(s => s.updateFlashcards);
    const deleteFlashcards = useSetEditingContextSelector(s => s.deleteFlashcards);
    const virtuosoRef = useSetEditingContextSelector(s => s.virtuosoRef);
    const flashcards = flashcardSet?.flashcards;

    useEffect(() => {
        if (!readOnly) return;
        if (flashcardSet?.flashcards) {
            setFullContent(
                JSON.stringify(
                    flashcardSet.flashcards.map(f => ({
                        term: htmlToMd(f?.term || ""),
                        definition: htmlToMd(f?.definition || ""),
                    }))
                )
            );
        }

        if ((!sourceContent && stringifiedTranscript) || note?.content) {
            setSourceContent(stringifiedTranscript || note?.content);
        }
    }, [
        setFullContent,
        sourceContent,
        setSourceContent,
        stringifiedTranscript,
        note?.content,
        flashcardSet?.flashcards,
        readOnly,
    ]);

    const lastChatHasFlashcards = useMemo(
        () => (chatLog?.[(chatLog?.length || 0) - 1]?.flashcards?.length || 0) > 0,
        [chatLog]
    );

    const isSetEmpty = useMemo(
        () =>
            flashcardSet?.flashcards?.every(
                flashcard =>
                    (!flashcard?.term && !flashcard?.definition) ||
                    flashcard?.term === "<p></p>" ||
                    flashcard?.definition === "<p></p>"
            ),
        [flashcardSet?.flashcards]
    );

    if (!flashcardSet) return null;

    const clearStates = () => {
        setChatFlowState(ChatFlowState.DEFAULT_ACTION);
        stagePrompt({ inputQuestion: "", prompt: "" });
    };

    const handleReplace = async () => {
        if (!lastChatHasFlashcards) return toast.error("Something went wrong, reload and try again");

        if (selectedContent) {
            actionToFlashcard({
                term: chatLog?.[chatLog?.length - 1]?.flashcards?.[0]?.term || "",
                definition: chatLog?.[chatLog?.length - 1]?.flashcards?.[0]?.definition || "",
                behavior: "replace",
            });

            clearStates();

            return;
        }

        const replaceAll = async () => {
            const newFlashcardsFromAi = chatLog?.[chatLog?.length - 1]?.flashcards as {
                term: string;
                definition: string;
            }[];

            const newFlashcards = await updateFlashcards(oldFlashcards =>
                oldFlashcards.map((flashcard, index) => ({
                    ...flashcard,
                    term: newFlashcardsFromAi[index]?.term || flashcard.term,
                    definition: newFlashcardsFromAi[index]?.definition || flashcard.definition,
                }))
            );

            if ((newFlashcardsFromAi?.length || 0) < (newFlashcards?.flashcards?.length || 0))
                await await deleteFlashcards(
                    flashcards
                        ?.slice(newFlashcardsFromAi.length)
                        .map(flashcard => flashcard?.flashcardId)
                        .filter(Boolean) as string[]
                );

            clearStates();

            rerenderFlashcardList();
        };

        openConfirmDialog({
            title: "Are you sure you want to replace all cards?",
            subtitle:
                "All the flashcards in this set will be replaced with the new ones, the old ones will be deleted.",
            style: { borderRadius: "4rem" },
            rounded: true,
            cardSize: "60rem",
            spacing: 2.2,
            subtitleFontSize: "1.9rem",
            subtitleFontWeight: "500",
            buttonColor: "black",
            buttonTextColor: "white",
            simpleCancelBtn: true,
            onConfirm: replaceAll,
        });
    };

    const handleAdd = async () => {
        if (!lastChatHasFlashcards) return toast.error("Something went wrong, reload and try again");

        setChatFlowState(ChatFlowState.DEFAULT_ACTION);

        const newIdx = flashcards?.length ?? 0;

        const newFlashcards = await addFlashcards({
            insertAt: newIdx,
            flashcards: chatLog?.[chatLog?.length - 1]?.flashcards as { term: string; definition: string }[],
        });

        if (!newFlashcards.length) return;
        setTimeout(() => virtuosoRef?.current?.scrollToIndex({ index: newIdx, behavior: "smooth" }), 25);
    };

    const suggestionBasicForEmptySet: ChatSuggestionOpt[] = [
        {
            label: "Create flashcards on... (insert topic)",
            icon: <SparkleIcon size={20} />,
            onClick: () => stagePrompt({ inputQuestion: "Create flashcards on " }),
        },

        {
            label: "Create fill in the the blank flashcards on... (insert topic)",
            icon: <SparkleIcon size={20} />,
            onClick: () => stagePrompt({ inputQuestion: "Create fill in the the blank flashcards on " }),
        },
        {
            label: "Create Q & A flashcards on... (insert topic)",
            icon: <SparkleIcon size={20} />,
            onClick: () => stagePrompt({ inputQuestion: "Create Q & A flashcards on " }),
        },
    ];

    const suggestionBasic: ChatSuggestionOpt[] = [
        {
            label: (
                <span style={{ textAlign: "left" }}>
                    Click on the
                    <SparkleIcon size={20} style={{ margin: `0 ${spacing.XS}`, marginBottom: "-0.7rem" }} />
                    button next to any flashcard to edit it individually.{" "}
                </span>
            ),
            icon: <CircleAlertIcon size={20} color={themeColors.warningYellow} />,
            style: { backgroundColor: themeColors.flashcardsLight, border: "none" },
        },
        {
            label: FlashcardChatOptionLabels.REGENERATE_FLASHCARDS,
            icon: <SparkleIcon size={20} />,
            onClick: () =>
                stagePrompt({
                    inputQuestion: "Regenerate all the flashcards with the following changes: ",
                    prompt: FlashcardChatOptionPrompts[FlashcardChatOptionLabels.REGENERATE_FLASHCARDS],
                }),
        },
        {
            label: FlashcardChatOptionLabels.CREATE_MORE_FLASHCARDS,
            icon: <SparkleIcon size={20} />,
            onClick: async () => {
                stagePrompt({
                    inputQuestion: "Create more flashcards on the following topics: ",
                    prompt: FlashcardChatOptionPrompts[FlashcardChatOptionLabels.CREATE_MORE_FLASHCARDS],
                });
            },
        },
        {
            label: "Create 1 new flashcard on... (insert topic)",
            icon: <SparkleIcon size={20} />,
            onClick: () => stagePrompt({ inputQuestion: "Create 1 new flashcard on " }),
        },
    ];

    const suggestionUponAnswer: ChatSuggestionOpt[] = [
        {
            label: "Add card(s) to the bottom",
            icon: <PlusIcon size={20} />,
            onClick: handleAdd,
        },
        {
            label: isEmptyFlashcardSelected
                ? "Add to the selected card"
                : isSetEmpty
                  ? "Add to the set"
                  : selectedContent
                    ? "Replace flashcard with this"
                    : "Replace flashcards with these",
            icon: <ReplaceIcon size={20} />,
            onClick: handleReplace,
        },
        {
            label: FlashcardChatOptionLabels.TRY_AGAIN_FLASHCARD_OUTPUT,
            icon: <RotateCw size={20} />,
            onClick: () =>
                stagePrompt({
                    inputQuestion: "Try again with the following changes: ",
                    prompt: FlashcardChatOptionPrompts[FlashcardChatOptionLabels.TRY_AGAIN_FLASHCARD_OUTPUT],
                }),
        },
        {
            label: "Cancel",
            icon: <Ban size={20} />,
            onClick: () => {
                setChatFlowState(ChatFlowState.DEFAULT_ACTION);
                handleUnselect();
            },
        },
    ];

    const suggestionOnSelected: ChatSuggestionOpt[] = [
        {
            label: FlashcardChatOptionLabels.MAKE_IT_MORE_DETAILED_FLASHCARDS,
            icon: <SparkleIcon size={20} />,
            onClick: async () =>
                await processChat({
                    inputQuestion: FlashcardChatOptionLabels.MAKE_IT_MORE_DETAILED_FLASHCARDS,
                    prompt: FlashcardChatOptionPrompts[FlashcardChatOptionLabels.MAKE_IT_MORE_DETAILED_FLASHCARDS],
                    quote: selectedContent,
                }),
        },
        {
            label: FlashcardChatOptionLabels.SUMMARIZE_THE_DEFINITION,
            icon: <SparkleIcon size={20} />,
            onClick: async () =>
                await processChat({
                    inputQuestion: FlashcardChatOptionLabels.SUMMARIZE_THE_DEFINITION,
                    prompt: FlashcardChatOptionPrompts[FlashcardChatOptionLabels.SUMMARIZE_THE_DEFINITION],
                    quote: selectedContent,
                }),
        },
        {
            label: FlashcardChatOptionLabels.REGENERATE_SELECTED_FLASHCARD,
            icon: <RotateCw size={20} />,
            onClick: async () =>
                await processChat({
                    inputQuestion: FlashcardChatOptionLabels.REGENERATE_SELECTED_FLASHCARD,
                    prompt: FlashcardChatOptionPrompts[FlashcardChatOptionLabels.REGENERATE_SELECTED_FLASHCARD],
                    quote: selectedContent,
                }),
        },
        {
            label: "Cancel selection",
            icon: <Ban size={20} />,
            onClick: handleUnselect,
        },
    ];

    const suggestionOnEmptyCardSelected: ChatSuggestionOpt[] = [
        {
            label: "Create 1 flashcard on... (insert topic)",
            icon: <SparkleIcon size={20} />,
            onClick: () =>
                stagePrompt({
                    inputQuestion: "Create a flashcard on ",
                }),
        },
        {
            label: "Create 1 fill in the the blank flashcard on... (insert topic)",
            icon: <SparkleIcon size={20} />,
            onClick: () => stagePrompt({ inputQuestion: "Create a fill in the the blank flashcard on " }),
        },
        {
            label: "Create 1 Q & A flashcard on... (insert topic)",
            icon: <SparkleIcon size={20} />,
            onClick: () => stagePrompt({ inputQuestion: "Create a Q & A flashcard on " }),
        },
        {
            label: "Cancel selection",
            icon: <Ban size={20} />,
            onClick: handleUnselect,
        },
    ];

    return (
        <FlexColumn style={{ marginTop: "auto", height: "100%" }}>
            <KaiChatInterface
                placeholder="Tell Kai how to make your flashcards better"
                suggestionOptions={{
                    basic: isSetEmpty ? suggestionBasicForEmptySet : suggestionBasic,
                    uponAnswerGenerated: suggestionUponAnswer,
                    onSelected: isEmptyFlashcardSelected ? suggestionOnEmptyCardSelected : suggestionOnSelected,
                }}
            />
        </FlexColumn>
    );
};

export default SetEditingChatInterface;
